class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static setLoading(loading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', String(loading));
    grid?.querySelector('.collection')?.classList.toggle('loading', loading);
    ['FacetDrawer', 'ProductCount', 'ProductCountMobile', 'ProductCountDesktop'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('loading', loading);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = (FacetFiltersForm.requestId || 0) + 1;
    FacetFiltersForm.requestId = requestId;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    const signal = FacetFiltersForm.abortController.signal;
    const error = document.getElementById('FacetUpdateError');
    if (error) {
      error.hidden = true;
      const retry = error.querySelector('[data-facet-retry]');
      if (retry) retry.href = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    }
    FacetFiltersForm.setLoading(true);
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    return Promise.all(FacetFiltersForm.getSections().map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;
      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId, signal);
    })).catch((error) => {
      if (requestId !== FacetFiltersForm.requestId || error.name === 'AbortError') return;
      console.error(error);
      const errorElement = document.getElementById('FacetUpdateError');
      if (errorElement) errorElement.hidden = false;
    }).finally(() => {
      if (requestId === FacetFiltersForm.requestId) FacetFiltersForm.setLoading(false);
    });
  }

  static applySection(html, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return false;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!parsedHTML.getElementById('ProductGridContainer')) throw new Error('Product results were not returned');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
    return true;
  }

  static renderSectionFromFetch(url, event, requestId, signal) {
    return fetch(url, { signal })
      .then(response => {
        if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (!FacetFiltersForm.applySection(html, event, requestId)) return;
        // Bound cache growth as customers explore many combinations.
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }].slice(-12);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    return Promise.resolve().then(() => FacetFiltersForm.applySection(html, event, requestId));
  }

  static renderProductGridContainer(html) {
    const parsedHTML = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    document.getElementById('ProductGridContainer').innerHTML = parsedHTML.getElementById('ProductGridContainer').innerHTML;
    
    const layoutSwitcher = document.querySelector('#FacetSortFiltersForm layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.list-view__item--active'));
  }

  static renderProductCount(html) {
    const parsedHTML = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const source = parsedHTML.getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerMobile = document.getElementById('ProductCountMobile');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerMobile) {
      containerMobile.innerHTML = count;
      containerMobile.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll('#FacetSortFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const facetDetailsElementsFromDom =
      document.querySelectorAll('#FacetSortFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId)?.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)?.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

    const facetDrawer = document.getElementById('FacetDrawer');
    if (facetDrawer) {
      facetDrawer.classList.remove('loading');
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.facets__open', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('facet-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData);
  }

  mergeSearchParams(form, searchParams) {
    const params = this.createSearchParams(form);
    params.forEach((value, key) => {
      if (key === 'sort_by' || key === 'q' || key === 'options[prefix]') {
        searchParams.set(key, value);
      } else if (!searchParams.getAll(key).includes(value)) {
        searchParams.append(key, value);
      }
    });
    return searchParams;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const currentForm = event.target.closest('form');
    if (currentForm.id === 'FacetFiltersFormMobile') {
      const searchParams = this.createSearchParams(currentForm);
      this.onSubmitForm(searchParams.toString(), event);
    }
    else {
      let searchParams = new URLSearchParams();

      if (currentForm.id === 'FacetSortFiltersForm' && currentForm.dataset.filterType === 'drawer') {
        const mobileForm = document.getElementById('FacetFiltersFormMobile');
        searchParams = this.mergeSearchParams(mobileForm, searchParams);
        searchParams.delete('sort_by');
      }
  
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetFiltersForm' || form.id === 'FacetSortFiltersForm') {
          searchParams = this.mergeSearchParams(form, searchParams);
        }
      });

      const currentSort = this.createSearchParams(currentForm).get('sort_by');
      if (currentSort) searchParams.set('sort_by', currentSort);
      this.onSubmitForm(searchParams.toString(), event);
    }
    /*
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
    */
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.renderPage(new URL(event.currentTarget.href).searchParams.toString());
  }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelector('a').addEventListener('click', (event) => {
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (!form) return;
      form.onActiveFilterClick(event);
    });
  }
}
customElements.define('facet-remove', FacetRemove);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.min = Number(this.dataset.min);
    this.max = Number(this.dataset.max);
    this.track = this.querySelector('.price-range__track');
    this.handles = [...this.querySelectorAll('.price-range__thumbs')];
    this.inputs = [...this.querySelectorAll('input')];
    this.moveListener = this.move.bind(this);
    this.stopListener = this.stopMove.bind(this);
    this.handles.forEach((handle, index) => {
      handle.addEventListener('pointerdown', this.startMove.bind(this));
      handle.addEventListener('keydown', event => this.onKeyDown(event, index));
    });
    this.inputs.forEach(input => input.addEventListener('change', this.onRangeChange.bind(this)));
  }

  connectedCallback() {
    this.setMinAndMaxValues();
    this.syncHandles();
  }

  disconnectedCallback() {
    this.removeDragListeners();
    this.activeHandle = null;
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncHandles();
    this.closest('form')?.dispatchEvent(new Event('input', { bubbles: true }));
  }

  startMove(event) {
    if (event.button !== 0 || !this.track) return;
    event.preventDefault();
    this.removeDragListeners();
    this.activeHandle = event.currentTarget;
    this.pointerId = event.pointerId;
    window.addEventListener('pointermove', this.moveListener);
    window.addEventListener('pointerup', this.stopListener);
    window.addEventListener('pointercancel', this.stopListener);
  }

  move(event) {
    if (!this.activeHandle || event.pointerId !== this.pointerId) return;
    const rect = this.track.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const input = this.inputs[this.activeHandle.classList.contains('is-lower') ? 0 : 1];
    input.value = Math.round(this.min + ratio * (this.max - this.min));
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
    this.syncHandles();
  }

  removeDragListeners() {
    window.removeEventListener('pointermove', this.moveListener);
    window.removeEventListener('pointerup', this.stopListener);
    window.removeEventListener('pointercancel', this.stopListener);
  }

  stopMove(event) {
    if (!this.activeHandle || event.pointerId !== this.pointerId) return;
    this.removeDragListeners();
    this.activeHandle = null;
    this.closest('form')?.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onKeyDown(event, index) {
    const input = this.inputs[index];
    const minimum = Number(input.getAttribute('min'));
    const maximum = Number(input.getAttribute('max'));
    let value = input.value === '' ? (index === 0 ? minimum : maximum) : Number(input.value);
    if (['ArrowRight', 'ArrowUp'].includes(event.key)) value += 1;
    else if (['ArrowLeft', 'ArrowDown'].includes(event.key)) value -= 1;
    else if (event.key === 'Home') value = minimum;
    else if (event.key === 'End') value = maximum;
    else return;
    event.preventDefault();
    input.value = String(Math.max(minimum, Math.min(maximum, value)));
    this.onRangeChange({ currentTarget: input });
  }

  setMinAndMaxValues() {
    const [lower, upper] = this.inputs;
    lower.setAttribute('min', this.min);
    lower.setAttribute('max', upper.value === '' ? this.max : Number(upper.value));
    upper.setAttribute('min', lower.value === '' ? this.min : Number(lower.value));
    upper.setAttribute('max', this.max);
  }

  adjustToValidValues(input) {
    if (input.value === '') return;
    const value = Number(input.value);
    if (!Number.isFinite(value)) { input.value = ''; return; }
    input.value = String(Math.max(Number(input.getAttribute('min')), Math.min(Number(input.getAttribute('max')), value)));
  }

  syncHandles() {
    this.handles.forEach((handle, index) => {
      const input = this.inputs[index];
      const value = input.value === '' ? (index === 0 ? this.min : this.max) : Number(input.value);
      handle.setAttribute('aria-valuemin', input.getAttribute('min'));
      handle.setAttribute('aria-valuemax', input.getAttribute('max'));
      handle.setAttribute('aria-valuenow', value);
      const percent = this.max > this.min ? (value - this.min) / (this.max - this.min) * 100 : 0;
      this.style.setProperty(index === 0 ? '--progress-lower' : '--progress-upper', percent + '%');
      const output = handle.nextElementSibling?.querySelector('.price-range__output-text');
      if (output) output.textContent = value;
    });
  }
}
customElements.define('price-range', PriceRange);

class LayoutSwitcher extends HTMLElement {
	constructor() {
    super();

    this.cookieName = 'beyours:collection-layout';

    this.initLayoutMode();
    this.querySelectorAll('.list-view__item').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
	}
	
  onButtonClick(event) {
    if (!event) return;
    const target = event.currentTarget || event.target?.closest('.list-view__item') || event;
    this.changeLayoutMode(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (isStorageSupported('local')) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`.list-view__item[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayoutMode(target, layoutMode);
        }
      }
    }
  }

  changeLayoutMode(target, layoutMode) {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !target) return;

    if (productGrid.classList.contains('collection--empty')) {
      return;
    }

    const removedClass = ['list', 'flex-grid', 'flex-grid--1-col', 'flex-grid--2-col', 'flex-grid--3-col-tablet', 'flex-grid--2-col-desktop', 'flex-grid--3-col-desktop', 'flex-grid--4-col-desktop'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = [];
    switch (layoutMode) {
      case 'list':
        addedClass = ['flex-grid', 'flex-grid--1-col', 'flex-grid--2-col-desktop', 'list'];
        break;

      case 'grid-2':
        addedClass = ['flex-grid', 'flex-grid--2-col'];
        break;

      case 'grid-3':
        addedClass = ['flex-grid', 'flex-grid--2-col', 'flex-grid--3-col-tablet', 'flex-grid--3-col-desktop'];
        break;

      case 'grid-4':
        addedClass = ['flex-grid', 'flex-grid--2-col', 'flex-grid--3-col-tablet', 'flex-grid--4-col-desktop'];
        break;
    }
    addedClass.forEach((added) => {
      productGrid.classList.add(added);
    });

    this.querySelectorAll('.list-view__item').forEach(
      (button) => button.classList.remove('list-view__item--active')
    );

    target.classList.add('list-view__item--active');

    if (isStorageSupported('local')) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);

class StickyFacetFilters extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.parentNode.offsetTop) {
      window.requestAnimationFrame(this.reveal.bind(this));
    } else {
      window.requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.classList.add('shopify-section-filters-sticky');
  }

  reset() {
    this.classList.remove('shopify-section-filters-sticky');
  }
}
customElements.define('sticky-facet-filters', StickyFacetFilters);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();

    const attributes = {
      expanded: 'aria-expanded'
    };

    const button = this.querySelector('.button-show-more');
    button.addEventListener('click', () => {
      const filter = this.closest('.js-filter');
      filter.setAttribute(
        attributes.expanded,
        (filter.getAttribute(attributes.expanded) === 'false').toString()
      );

      this.querySelectorAll('.visually-hidden').forEach(element => element.classList.toggle('hidden'));
    });
  }
}
customElements.define('show-more-button', ShowMoreButton);
